import { Modal } from "react-bootstrap";
import withModal from "../../components/withModal";
import CustomSelect from "../form/CustomSelect";
import CommonButton from "../commonButton";

/**
 * Common Filter Modal component.
 * Supports dynamic fields based on provided options and handles single/multi-select automatically.
 */
const FilterModalInner = ({
    onClose,
    filterValues,
    setFilterValues,
    setCurrentPage,
    assetOptions,
    propertyOptions,
    possessionOptions,
    cityOptions,
    professionOptions,
    categoryOptions,
    reservePriceOptions,
    transactionOptions,
    statusOptions,
    landTypeOptions,
    onClear,
    onCityInputChange,
}) => {
    // Determine if fields should be multi-select based on their current value types in filterValues
    const isMultiProperty = Array.isArray(filterValues.property);
    const isMultiPossession = Array.isArray(filterValues.possession);
    const isMultiTransaction = Array.isArray(filterValues.transaction);
    const isMultiStatus = Array.isArray(filterValues.status);
    const isMultiProfession = Array.isArray(filterValues.profession);
    const isMultiCity = Array.isArray(filterValues.city);
    const isMultiCategory = Array.isArray(filterValues.category);

    const handleClear = () => {
        if (onClear) {
            onClear();
        } else {
            setFilterValues({
                search: "",
                asset: "",
                property: isMultiProperty ? [] : "",
                possession: isMultiPossession ? [] : "",
                city: isMultiCity ? [] : "",
                reservePrice: "",
                transaction: isMultiTransaction ? [] : "",
                status: isMultiStatus ? [] : "",
                profession: isMultiProfession ? [] : "",
                category: isMultiCategory ? [] : "",
                land_types: "",
            });
            setCurrentPage(1);
            onClose();
        }
    };

    return (
        <>
            <Modal.Body className="grid gap-4">

                {assetOptions && (
                    <CustomSelect
                        label={{ name: "Asset category" }}
                        options={assetOptions}
                        value={assetOptions?.find((opt) => opt.value === filterValues.asset) || null}
                        isClearable
                        placeholder="Asset category"
                        onChange={(e) => {
                            setFilterValues((val) => ({ ...val, asset: e?.value || "" }));
                            setCurrentPage(1);
                        }}
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                    />
                )}

                {/* Transaction Type */}
                {transactionOptions && (

                    <CustomSelect
                        options={transactionOptions}
                        value={
                            isMultiTransaction
                                ? transactionOptions?.filter((opt) => filterValues.transaction?.includes(opt.value))
                                : transactionOptions?.find((opt) => opt.value === filterValues.transaction) || null
                        }
                        isClearable
                        isMulti={isMultiTransaction}
                        placeholder="Transaction Type"
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        onChange={(selected) => {
                            const value = isMultiTransaction
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, transaction: value }));
                            setCurrentPage(1);
                        }}
                        label={{ name: "Transaction Type" }}
                    />

                )}

                {/* Property Type */}
                {propertyOptions && (

                    <CustomSelect
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        options={propertyOptions}
                        value={
                            isMultiProperty
                                ? propertyOptions?.filter((opt) => filterValues.property?.includes(opt.value))
                                : propertyOptions?.find((opt) => opt.value === filterValues.property) || null
                        }
                        isClearable
                        isMulti={isMultiProperty}
                        placeholder="Property Type"
                        onChange={(selected) => {
                            const value = isMultiProperty
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";

                            setFilterValues((val) => {
                                const hasLand = isMultiProperty
                                    ? value.includes("Land")
                                    : value === "Land";
                                return {
                                    ...val,
                                    property: value,
                                    land_types: hasLand ? val.land_types : "",
                                };
                            });
                            setCurrentPage(1);
                        }}
                        label={{ name: "Property Type" }}
                    />

                )}

                {/* Land Types (Conditional) */}
                {landTypeOptions && (isMultiProperty ? filterValues.property?.includes("Land") : filterValues.property === "Land") && (

                    <CustomSelect
                        options={landTypeOptions}
                        value={
                            filterValues?.land_types
                                ? { value: filterValues?.land_types, label: filterValues?.land_types }
                                : null
                        }
                        isClearable
                        placeholder="Land Types"
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        onChange={(e) => {
                            setFilterValues((val) => ({ ...val, land_types: e?.value || "" }));
                            setCurrentPage(1);
                        }}
                        label={{ name: "Land Types" }}
                    />

                )}

                {/* Possession Type */}
                {possessionOptions && (

                    <CustomSelect
                        className="g-col-12"
                        options={possessionOptions}
                        value={
                            isMultiPossession
                                ? possessionOptions?.filter((opt) => filterValues.possession?.includes(opt.value))
                                : possessionOptions?.find((opt) => opt.value === filterValues.possession) || null
                        }
                        label={{ name: "Possession Type" }}
                        isClearable
                        isMulti={isMultiPossession}
                        placeholder="Possession Type"
                        onChange={(selected) => {
                            const value = isMultiPossession
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, possession: value }));
                            setCurrentPage(1);
                        }}
                    />

                )}

                {/* City */}
                {cityOptions && (

                    <CustomSelect
                        options={cityOptions}
                        value={
                            isMultiCity
                                ? cityOptions?.filter((opt) => filterValues.city?.includes(opt.value))
                                : cityOptions?.find((opt) => opt.value === filterValues.city) || null
                        }
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        label={{ name: "City" }}
                        isClearable
                        isMulti={isMultiCity}
                        placeholder="City"
                        onInputChange={onCityInputChange}
                        onChange={(selected) => {
                            const value = isMultiCity
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, city: value }));
                            setCurrentPage(1);
                        }}
                    />

                )}

                {/* Profession */}
                {professionOptions && (

                    <CustomSelect
                        options={professionOptions}
                        label={{ name: "Profession" }}
                        value={
                            isMultiProfession
                                ? professionOptions?.filter((opt) => filterValues.profession?.includes(opt.value))
                                : professionOptions?.find((opt) => opt.value === filterValues.profession) || null
                        }
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        isClearable
                        isMulti={isMultiProfession}
                        placeholder="Profession"
                        onChange={(selected) => {
                            const value = isMultiProfession
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, profession: value }));
                            setCurrentPage(1);
                        }}
                    />

                )}

                {/* Category */}
                {categoryOptions && (

                    <CustomSelect
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        options={categoryOptions}
                        value={
                            isMultiCategory
                                ? categoryOptions?.filter((opt) => filterValues.category?.includes(opt.value))
                                : categoryOptions?.find((opt) => opt.value === filterValues.category) || null
                        }
                        label={{ name: "Category" }}
                        isClearable
                        isMulti={isMultiCategory}
                        placeholder="Category"
                        onChange={(selected) => {
                            const value = isMultiCategory
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, category: value }));
                            setCurrentPage(1);
                        }}
                    />

                )}

                {/* Reserve Price */}
                {reservePriceOptions && (

                    <CustomSelect
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        options={reservePriceOptions}
                        value={
                            filterValues?.reservePrice
                                ? { value: filterValues?.reservePrice, label: filterValues?.reservePrice }
                                : null
                        }
                        label={{ name: "Reserve Price" }}
                        isClearable
                        placeholder="Reserve Price"
                        onChange={(e) => {
                            setFilterValues((val) => ({ ...val, reservePrice: e?.value || "" }));
                            setCurrentPage(1);
                        }}
                    />

                )}

                {/* Status */}
                {statusOptions && (

                    <CustomSelect
                        className="g-col-12 g-col-md-6 g-col-lg-4"
                        options={statusOptions}
                        value={
                            isMultiStatus
                                ? statusOptions?.filter((opt) => filterValues.status?.includes(opt.value))
                                : statusOptions?.find((opt) => opt.value === filterValues.status) || null
                        }
                        isClearable
                        isMulti={isMultiStatus}
                        placeholder="Status"
                        label={{ name: "Status" }}
                        onChange={(selected) => {
                            const value = isMultiStatus
                                ? (selected || []).map((o) => o.value)
                                : selected?.value || "";
                            setFilterValues((val) => ({ ...val, status: value }));
                            setCurrentPage(1);
                        }}
                    />

                )}

            </Modal.Body>

            <Modal.Footer>
                <CommonButton
                    type="button"
                    variant="outline"
                    onClick={handleClear}
                    title=" Clear all filters"
                />
            </Modal.Footer>
        </>
    );
};

const FilterModal = withModal(FilterModalInner);
export default FilterModal;